/**
 * src/lib/api/voice.js — Voice pipeline client API
 * Talks to /api/voice (GET for reads, POST for writes)
 */

import { apiFetch, API_BASE } from './client';

const VOICE = `${API_BASE}/ai`;

// ── Upload & Transcription ──────────────────────────────────────────

export async function uploadVoiceNote({
  audioBase64,
  mimeType,
  noteType,
  classifierProfile,
  projectId,
  crmDealId,
  sourceContext,
  language,
  idempotencyKey,
  durationSeconds,
  fileSizeBytes,
  storagePath,
  chunkIndex,
}) {
  // TICKET-1045: carries idempotency_key -- make explicit that a client.js
  // retry must not fire a second copy of this metered upload+STT call.
  return apiFetch(VOICE, {
    method: 'POST',
    noRetry: true,
    body: JSON.stringify({
      action: 'voice.upload',
      audio_base64: audioBase64,
      mime_type: mimeType,
      note_type: noteType,
      classifier_profile: classifierProfile,
      project_id: projectId,
      crm_deal_id: crmDealId,
      source_context: sourceContext,
      language,
      idempotency_key: idempotencyKey,
      duration_seconds: durationSeconds,
      file_size_bytes: fileSizeBytes,
      storage_path: storagePath,
      chunk_index: chunkIndex,
    }),
  });
}

export async function transcribeChunk({ voiceRecordId, chunkIndex, audioBase64, mimeType }) {
  // TICKET-1045: (voiceRecordId, chunkIndex) is the dedupe key server-side --
  // don't let a client.js retry fire a second metered transcription.
  return apiFetch(VOICE, {
    method: 'POST',
    noRetry: true,
    body: JSON.stringify({
      action: 'voice.transcribe',
      voice_record_id: voiceRecordId,
      chunk_index: chunkIndex,
      audio_base64: audioBase64,
      mime_type: mimeType,
    }),
  });
}

export async function finalizeTranscription(voiceRecordId) {
  return apiFetch(VOICE, {
    method: 'POST',
    body: JSON.stringify({ action: 'voice.finalize_transcription', voice_record_id: voiceRecordId }),
  });
}

// ── Classification ──────────────────────────────────────────────────

export async function classifyVoiceNote(voiceRecordId, classifierProfile) {
  // TICKET-1024: metered Gemini classification, non-idempotent server-side --
  // a client.js retry after a late 5xx would re-run the LLM call. noRetry
  // stops that; idempotency_key (the voice_record_id) gives a future
  // server-side dedupe check something to key on.
  return apiFetch(VOICE, {
    method: 'POST',
    noRetry: true,
    body: JSON.stringify({
      action: 'voice.classify',
      voice_record_id: voiceRecordId,
      classifier_profile: classifierProfile,
      idempotency_key: voiceRecordId,
    }),
  });
}

// ── Queries ─────────────────────────────────────────────────────────

export async function fetchVoiceNotes({ projectId, crmDealId, status, limit } = {}) {
  const params = new URLSearchParams({ action: 'voice.list' });
  if (projectId) params.set('project_id', projectId);
  if (crmDealId) params.set('crm_deal_id', crmDealId);
  if (status) params.set('status', status);
  if (limit) params.set('limit', String(limit));
  const data = await apiFetch(`${VOICE}?${params}`);
  return data?.voice_notes || [];
}

export async function fetchVoiceNote(id) {
  const params = new URLSearchParams({ action: 'voice.detail', id });
  return apiFetch(`${VOICE}?${params}`);
}

// ── Transcript Editing ──────────────────────────────────────────────

/**
 * Update the transcript text for a voice note. When `reprocess` is true,
 * also re-runs classification against the new text and refreshes the
 * derived_actions list. Untouched pending suggestions are marked
 * 'superseded'; user-edited / accepted / dismissed actions are preserved.
 */
export async function updateTranscript(voiceRecordId, editedTranscript, { reprocess = false } = {}) {
  return apiFetch(VOICE, {
    method: 'POST',
    body: JSON.stringify({
      action: 'voice.update_transcript',
      voice_record_id: voiceRecordId,
      edited_transcript: editedTranscript,
      reprocess,
    }),
  });
}
